if (!customElements.get('mp-solutions')) {
  customElements.define('mp-solutions', class extends HTMLElement {
    connectedCallback() {
      if (this._controller) return;
      this._track = this.querySelector('[data-solutions-track]');
      this._cards = Array.from(this.querySelectorAll('[data-solution-card]'));
      if (!this._track || !this._cards.length) return;
      this._controller = new AbortController();
      this._previous = this.querySelector('[data-solutions-prev]');
      this._next = this.querySelector('[data-solutions-next]');
      this._status = this.querySelector('[data-solutions-status]');
      this._active = Math.max(0, this._cards.findIndex(card => card.classList.contains('is-active')));
      this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const signal = this._controller.signal;
      this._previous?.addEventListener('click', () => this._goTo(this._active - 1), { signal });
      this._next?.addEventListener('click', () => this._goTo(this._active + 1), { signal });
      this._track.addEventListener('scroll', () => this._queueUpdate(), { signal, passive: true });
      this._track.addEventListener('keydown', event => {
        if (event.target !== this._track || event.altKey || event.ctrlKey || event.metaKey) return;
        const targets = { ArrowLeft: this._active - 1, ArrowRight: this._active + 1, Home: 0, End: this._cards.length - 1 };
        if (!(event.key in targets)) return;
        event.preventDefault();
        this._goTo(targets[event.key]);
      }, { signal });
      this._track.addEventListener('focusin', event => {
        const card = event.target.closest('[data-solution-card]');
        const index = this._cards.indexOf(card);
        if (index >= 0 && index !== this._active) this._goTo(index);
      }, { signal });
      this._track.addEventListener('toggle', event => {
        if (!event.target.matches('details') || !event.target.open) return;
        const index = this._cards.indexOf(event.target.closest('[data-solution-card]'));
        if (index >= 0) this._goTo(index);
      }, { signal, capture: true });
      document.addEventListener('shopify:block:select', event => {
        if (!(event.target instanceof Element)) return;
        const card = event.target.closest('[data-solution-card]');
        const index = this._cards.indexOf(card);
        if (index < 0) return;
        this._goTo(index, 'auto');
        const details = card.querySelector('details');
        if (details) details.open = true;
      }, { signal });
      this._resize = new ResizeObserver(() => {
        cancelAnimationFrame(this._resizeFrame);
        this._resizeFrame = requestAnimationFrame(() => this._goTo(this._active, 'auto'));
      });
      this._resize.observe(this._track);
      [this._previous, this._next].forEach(button => {
        if (button) button.hidden = this._cards.length < 2;
      });
      this.dataset.solutionsReady = 'true';
      this._goTo(this._active, 'auto');
    }

    disconnectedCallback() {
      this._controller?.abort();
      this._controller = null;
      this._resize?.disconnect();
      cancelAnimationFrame(this._frame);
      cancelAnimationFrame(this._resizeFrame);
      this._frame = null;
      this._resizeFrame = null;
      delete this.dataset.solutionsReady;
      [this._previous, this._next].forEach(button => {
        if (button) button.hidden = true;
      });
    }

    _goTo(index, behavior) {
      if (!this.isConnected || !this._track) return;
      index = Math.max(0, Math.min(index, this._cards.length - 1));
      const card = this._cards[index];
      const cardBounds = card.getBoundingClientRect();
      const trackBounds = this._track.getBoundingClientRect();
      const center = trackBounds.left + this._track.clientWidth / 2;
      const left = this._track.scrollLeft + cardBounds.left + cardBounds.width / 2 - center;
      this._setActive(index);
      this._track.scrollTo({ left: Math.max(0, left), behavior: behavior || (this._motion.matches ? 'auto' : 'smooth') });
      this._queueUpdate();
    }

    _queueUpdate() {
      if (this._frame) return;
      this._frame = requestAnimationFrame(() => {
        this._frame = null;
        if (!this.isConnected) return;
        const bounds = this._track.getBoundingClientRect();
        const center = bounds.left + this._track.clientWidth / 2;
        let nearest = 0;
        let distance = Infinity;
        this._cards.forEach((card, index) => {
          const rect = card.getBoundingClientRect();
          const current = Math.abs(rect.left + rect.width / 2 - center);
          if (current < distance) {
            distance = current;
            nearest = index;
          }
        });
        this._setActive(nearest);
      });
    }

    _setActive(index) {
      this._active = index;
      this._cards.forEach((card, cardIndex) => card.classList.toggle('is-active', cardIndex === index));
      if (this._previous) this._previous.disabled = index === 0;
      if (this._next) this._next.disabled = index === this._cards.length - 1;
      if (this._status) {
        const title = this._cards[index].querySelector('h3')?.textContent?.trim() || '';
        const text = `${index + 1} / ${this._cards.length}${title ? ` — ${title}` : ''}`;
        if (this._status.textContent !== text) this._status.textContent = text;
      }
    }
  });
}
